import localeRegistryJson from '../../i18n/locales.generated.json' with { type: 'json' };

export type TextDirection = 'ltr' | 'rtl';
export type LocaleStage = 'planned' | 'preview' | 'live' | 'retired';

type LocaleDefinition = {
    tag: string;
    englishName: string;
    nativeName: string;
    direction: TextDirection;
    fallback: string | null;
    pathPrefix: string;
    stage: LocaleStage;
    indexable: boolean;
};

type TestLocaleDefinition = {
    tag: string;
    kind: 'pseudo';
    direction: TextDirection;
    fallback: string;
};

type LocaleRegistry = {
    schemaVersion: 1;
    sourceLocale: string;
    defaultLocale: string;
    defaultFormatLocale: string;
    urlPolicy: {
        prefixDefaultLocale: boolean;
    };
    locales: LocaleDefinition[];
    testLocales: TestLocaleDefinition[];
};

const localeRegistry = localeRegistryJson as LocaleRegistry;
const managedLocaleDefinitions = new Map(
    localeRegistry.locales.map((definition) => [definition.tag, definition])
);
const liveLocaleDefinitions = new Map(
    localeRegistry.locales
        .filter((definition) => definition.stage === 'live')
        .map((definition) => [definition.tag, definition])
);
const testLocaleDefinitions = new Map(
    localeRegistry.testLocales.map((definition) => [definition.tag, definition])
);

export const SOURCE_LOCALE = localeRegistry.sourceLocale;
export const DEFAULT_LOCALE = localeRegistry.defaultLocale;
export const DEFAULT_FORMAT_LOCALE = localeRegistry.defaultFormatLocale;
export const I18N_BUILD_TARGET_ENV = 'EXPO_PUBLIC_PZ_I18N_BUILD_TARGET' as const;
export const I18N_REVIEW_ARTIFACT_ENV = 'EXPO_PUBLIC_PZ_I18N_REVIEW_ARTIFACT' as const;
export const PRODUCT_LANGUAGE_ACTIVATION_ENV = 'EXPO_PUBLIC_PZ_PRODUCT_LANGUAGE_ACTIVATION' as const;

export type I18nBuildTarget = 'production' | 'beta' | 'review';
export type ProductLanguageActivation = 'disabled' | 'enabled';

export function getI18nBuildTarget(
    value = process.env.EXPO_PUBLIC_PZ_I18N_BUILD_TARGET
): I18nBuildTarget {
    if (value === undefined || value === '' || value === 'production') {
        return 'production';
    }
    if (value === 'beta' || value === 'review') {
        return value;
    }
    throw new Error(`Unsupported i18n build target: ${value}`);
}

export function isNonPromotableReviewArtifact(
    value = process.env.EXPO_PUBLIC_PZ_I18N_REVIEW_ARTIFACT
): boolean {
    return getI18nBuildTarget() === 'review' && value === 'non-promotable';
}

export function getProductLanguageActivation(
    value = process.env.EXPO_PUBLIC_PZ_PRODUCT_LANGUAGE_ACTIVATION
): ProductLanguageActivation {
    if (value === undefined || value === '' || value === 'disabled') return 'disabled';
    if (value === 'enabled') return 'enabled';
    throw new Error(`Unsupported product-language activation: ${value}`);
}

/** Only the explicit second production artifact may read or write profile language. */
export function canActivatePersistedProductLanguage(
    target = getI18nBuildTarget(),
    activation = getProductLanguageActivation()
): boolean {
    return target === 'production' && activation === 'enabled';
}

/**
 * Locale-stage behavior shared by web, native, and deterministic build tests.
 * A review build is deliberately a separate, permanently non-promotable class.
 */
export function isLocaleStageVisibleForBuild(
    stage: LocaleStage,
    target: I18nBuildTarget,
    nonPromotableReviewArtifact = false
): boolean {
    if (stage === 'retired') {
        return false;
    }
    if (target === 'production') {
        return stage === 'live';
    }
    if (target === 'beta') {
        return stage === 'live' || stage === 'preview';
    }
    if (!nonPromotableReviewArtifact) {
        return false;
    }
    return stage === 'live' || stage === 'preview' || stage === 'planned';
}

function runtimeLocaleDefinitions(
    target = getI18nBuildTarget(),
    reviewArtifact = isNonPromotableReviewArtifact()
): Map<string, LocaleDefinition> {
    if (target === 'review' && !reviewArtifact) {
        throw new Error(
            'The review i18n build target requires an explicit non-promotable review artifact marker'
        );
    }
    return new Map(
        localeRegistry.locales
            .filter((definition) => (
                isLocaleStageVisibleForBuild(definition.stage, target, reviewArtifact)
            ))
            .map((definition) => [definition.tag, definition])
    );
}

export function getRuntimeLocales(
    target = getI18nBuildTarget(),
    reviewArtifact = isNonPromotableReviewArtifact()
): string[] {
    return Array.from(runtimeLocaleDefinitions(target, reviewArtifact).keys());
}

export const MANAGED_CATALOG_LOCALES = localeRegistry.locales
    .filter((definition) => definition.stage !== 'retired')
    .map((definition) => definition.tag);
export const RUNTIME_LOCALES = getRuntimeLocales();
export const PRODUCTION_LOCALES = Array.from(liveLocaleDefinitions.keys());
export const LIVE_MESSAGE_LOCALES = Array.from(liveLocaleDefinitions.keys());
export const TEST_LOCALES = localeRegistry.testLocales.map((definition) => definition.tag);
export const PRODUCT_LANGUAGE_TAGS = ['en', 'es'] as const;

export type ProductionLocale = 'en' | 'es';
export type MessageLocale = ProductionLocale | 'en-XA';

export type ProductLanguageOption = {
    tag: ProductionLocale;
    nativeName: string;
};

export function getSelectableProductLanguages(): ProductLanguageOption[] {
    const visible = runtimeLocaleDefinitions();
    const privateActivationEnabled = canActivatePersistedProductLanguage();
    return PRODUCT_LANGUAGE_TAGS.flatMap((tag) => {
        const definition = visible.get(tag);
        const canSelect = definition && (
            getI18nBuildTarget() !== 'production'
            || privateActivationEnabled
            || tag === DEFAULT_LOCALE
        );
        return canSelect ? [{ tag, nativeName: definition.nativeName }] : [];
    });
}

export type LocaleResolutionSource = 'build' | 'profile' | 'device' | 'default';

export type ResolvedAppLocale = {
    requestedTag: string | null;
    messageLocale: MessageLocale;
    formatLocale: string;
    direction: TextDirection;
    source: LocaleResolutionSource;
    fallbackUsed: boolean;
};

export type ResolveAppLocaleInput = {
    buildLocale?: string | null;
    profileLanguageTag?: string | null;
    detectedLocaleTags?: readonly string[];
};

export const LOCALE_SNAPSHOT_SCHEMA_VERSION = 1 as const;
export const AUTH_MESSAGE_LOCALE_METADATA_KEY = 'paperzilla_auth_message_locale' as const;
export const PRODUCT_LANGUAGE_AUTHORITY_METADATA_KEY = 'paperzilla_product_language_authority' as const;
const PRODUCT_LANGUAGE_AUTHORITY_PREFIX = 'profile-v1:';

export type LocaleSnapshotSource = 'preferred' | 'detected' | 'default';

export type LocaleSnapshotV1 = {
    schema_version: typeof LOCALE_SNAPSHOT_SCHEMA_VERSION;
    requested_tag: string | null;
    format_tag: string;
    message_locale: string;
    source: LocaleSnapshotSource;
    message_fallback: boolean;
};

export type ResolveLocaleSnapshotInput = {
    preferredLanguageTag?: string | null;
    detectedPrimaryLocale?: string | null;
};

export type ResolveAuthMessageLocaleInput = ResolveLocaleSnapshotInput & {
    existingMessageLocale?: string | null;
};

export function canonicalizeLocaleTag(value: string | null | undefined): string | null {
    if (typeof value !== 'string' || value.trim().length === 0) {
        return null;
    }

    const compatibilityTag = value.trim().replaceAll('_', '-');
    const paperzillaLocalePattern = /^[A-Za-z]{2,8}(?:-[A-Za-z]{4})?(?:-(?:[A-Za-z]{2}|[0-9]{3}))?$/;
    if (!paperzillaLocalePattern.test(compatibilityTag)) {
        return null;
    }

    try {
        return Intl.getCanonicalLocales(compatibilityTag)[0] ?? null;
    } catch {
        return null;
    }
}

function matchExactLocaleTag(
    value: string | null | undefined,
    candidates: ReadonlyMap<string, unknown>
): string | null {
    const canonicalValue = canonicalizeLocaleTag(value);
    if (!canonicalValue) {
        return null;
    }

    for (const candidate of candidates.keys()) {
        if (canonicalizeLocaleTag(candidate) === canonicalValue) {
            return candidate;
        }
    }

    return null;
}

function matchSupportedLocaleTag(
    value: string | null | undefined,
    candidates: ReadonlyMap<string, unknown>
): string | null {
    const canonicalValue = canonicalizeLocaleTag(value);
    if (!canonicalValue || matchExactLocaleTag(canonicalValue, testLocaleDefinitions)) {
        return null;
    }

    const exactMatch = matchExactLocaleTag(canonicalValue, candidates);
    if (exactMatch) {
        return exactMatch;
    }

    const requestedLocale = new Intl.Locale(canonicalValue);
    const maximizedRequestedLocale = requestedLocale.maximize();
    const languageScript = maximizedRequestedLocale.script
        ? `${requestedLocale.language}-${maximizedRequestedLocale.script}`
        : null;
    if (languageScript) {
        const scriptMatch = matchExactLocaleTag(languageScript, candidates);
        if (scriptMatch) {
            return scriptMatch;
        }
    }

    const languageMatch = matchExactLocaleTag(requestedLocale.language, candidates);
    if (languageMatch) {
        return languageMatch;
    }

    return null;
}

/** Testable form of the shared exact → language-script → language matcher. */
export function matchLocaleTagFromCandidates(
    value: string | null | undefined,
    candidates: readonly string[]
): string | null {
    return matchSupportedLocaleTag(
        value,
        new Map(candidates.map((candidate) => [candidate, true]))
    );
}

function matchRuntimeLocaleTag(value: string | null | undefined): ProductionLocale | null {
    return matchSupportedLocaleTag(
        value,
        runtimeLocaleDefinitions()
    ) as ProductionLocale | null;
}

export function matchLiveMessageLocaleTag(value: string | null | undefined): string | null {
    return matchSupportedLocaleTag(value, liveLocaleDefinitions);
}

/**
 * Return the exact canonical detected tag only when its messages are live and
 * the current artifact can render them. Unsupported locales are never stored.
 */
export function getAutoPersistableDetectedLanguageTag(
    detectedLocaleTags: readonly string[]
): string | null {
    if (!canActivatePersistedProductLanguage()) return null;
    return resolveAutoPersistableDetectedLanguageTag(
        detectedLocaleTags,
        Array.from(liveLocaleDefinitions.keys()),
        getRuntimeLocales(),
        DEFAULT_LOCALE
    );
}

/** Testable locale negotiation used by device-preference auto-persistence. */
export function resolveAutoPersistableDetectedLanguageTag(
    detectedLocaleTags: readonly string[],
    liveMessageLocales: readonly string[],
    runtimeLocales: readonly string[],
    defaultLocale = DEFAULT_LOCALE
): string | null {
    const liveCandidates = new Map(liveMessageLocales.map((tag) => [tag, true]));
    const runtimeCandidates = new Map(runtimeLocales.map((tag) => [tag, true]));
    for (const value of detectedLocaleTags) {
        const canonical = canonicalizeLocaleTag(value);
        if (!canonical) continue;
        const liveMessageLocale = matchSupportedLocaleTag(canonical, liveCandidates);
        if (!liveMessageLocale) continue;
        if (matchSupportedLocaleTag(canonical, runtimeCandidates) !== liveMessageLocale) {
            continue;
        }
        return liveMessageLocale === defaultLocale ? null : canonical;
    }
    return null;
}

/**
 * Recover an established product language when an authenticated native client
 * cannot read its profile. This result is read-only. An exact cached
 * preference preserves regional formatting. Message-only metadata is never
 * authoritative; a versioned profile authority marker may recover an exact
 * established tag when older sessions do not contain the preference field.
 */
export function resolveReadOnlyCachedProductLanguageTag({
    cachedPreferredLanguageTag,
    cachedMessageLocale,
    cachedProductLanguageAuthority,
    liveMessageLocales = LIVE_MESSAGE_LOCALES,
    runtimeLocales = RUNTIME_LOCALES,
}: {
    cachedPreferredLanguageTag?: string | null;
    cachedMessageLocale?: string | null;
    cachedProductLanguageAuthority?: string | null;
    liveMessageLocales?: readonly string[];
    runtimeLocales?: readonly string[];
}): string | null {
    const liveCandidates = new Map(liveMessageLocales.map((tag) => [tag, true]));
    const runtimeCandidates = new Map(runtimeLocales.map((tag) => [tag, true]));
    const supported = (value: string | null | undefined) => {
        const canonical = canonicalizeLocaleTag(value);
        if (!canonical) return null;
        const messageLocale = matchSupportedLocaleTag(canonical, liveCandidates);
        if (
            !messageLocale
            || matchSupportedLocaleTag(canonical, runtimeCandidates) !== messageLocale
        ) {
            return null;
        }
        return { canonical, messageLocale };
    };

    const cachedPreference = supported(cachedPreferredLanguageTag);
    if (cachedPreference) return cachedPreference.canonical;

    if (
        typeof cachedProductLanguageAuthority !== 'string'
        || !cachedProductLanguageAuthority.startsWith(PRODUCT_LANGUAGE_AUTHORITY_PREFIX)
    ) {
        return null;
    }
    const authorityTag = supported(
        cachedProductLanguageAuthority.slice(PRODUCT_LANGUAGE_AUTHORITY_PREFIX.length)
    );
    const cachedMessage = supported(cachedMessageLocale);
    if (
        !authorityTag
        || (cachedMessageLocale != null && !cachedMessage)
        || (cachedMessage && cachedMessage.messageLocale !== authorityTag.messageLocale)
    ) {
        return null;
    }
    return authorityTag.canonical;
}

export function buildPersistedProductLanguageAuthority(
    preferredLanguageTag: string | null | undefined
): string | null {
    const canonical = canonicalizeLocaleTag(preferredLanguageTag);
    return canonical ? `${PRODUCT_LANGUAGE_AUTHORITY_PREFIX}${canonical}` : null;
}

export function isRuntimeLocale(value: string | null | undefined): boolean {
    return matchRuntimeLocaleTag(value) !== null;
}

export function isTestLocale(value: string | null | undefined): boolean {
    return matchExactLocaleTag(value, testLocaleDefinitions) !== null;
}

export function getBuildLocaleOverride(
    value = process.env.EXPO_PUBLIC_PZ_I18N_LOCALE
): MessageLocale | null {
    const runtimeOverride = (
        matchExactLocaleTag(value, testLocaleDefinitions)
        ?? matchRuntimeLocaleTag(value)
    ) as MessageLocale | null;
    if (runtimeOverride) {
        return runtimeOverride;
    }

    const managedOverride = matchExactLocaleTag(value, managedLocaleDefinitions);
    if (managedOverride) {
        throw new Error(
            `i18n build locale ${managedOverride} is not runtime-visible while its stage is ${managedLocaleDefinitions.get(managedOverride)?.stage}`
        );
    }
    return null;
}

export function getStaticBuildLocale(): MessageLocale {
    return (getBuildLocaleOverride() ?? DEFAULT_LOCALE) as MessageLocale;
}

export function getLocaleDirection(locale: MessageLocale): TextDirection {
    return managedLocaleDefinitions.get(locale)?.direction
        ?? testLocaleDefinitions.get(locale)?.direction
        ?? 'ltr';
}

export function getFormatLocale(locale: MessageLocale): string {
    if (locale === DEFAULT_LOCALE || testLocaleDefinitions.has(locale)) {
        return DEFAULT_FORMAT_LOCALE;
    }
    return locale;
}

function resolveRequestedTag(
    requestedTag: string,
    source: Exclude<LocaleResolutionSource, 'default'>,
    allowTestLocale: boolean
): ResolvedAppLocale {
    const testLocale = allowTestLocale
        ? matchExactLocaleTag(requestedTag, testLocaleDefinitions) as MessageLocale | null
        : null;
    const productionLocale = matchRuntimeLocaleTag(requestedTag);
    const messageLocale = testLocale
        ?? productionLocale
        ?? DEFAULT_LOCALE as MessageLocale;

    return {
        requestedTag,
        messageLocale,
        formatLocale: requestedTag,
        direction: getLocaleDirection(messageLocale),
        source,
        fallbackUsed: testLocale === null && productionLocale === null,
    };
}

export function resolveAppLocale({
    buildLocale,
    profileLanguageTag,
    detectedLocaleTags = [],
}: ResolveAppLocaleInput): ResolvedAppLocale {
    const canonicalBuildLocale = canonicalizeLocaleTag(buildLocale);
    if (canonicalBuildLocale) {
        const managedBuildLocale = matchExactLocaleTag(
            canonicalBuildLocale,
            managedLocaleDefinitions
        );
        if (
            managedBuildLocale
            && !matchExactLocaleTag(managedBuildLocale, runtimeLocaleDefinitions())
        ) {
            throw new Error(
                `i18n build locale ${managedBuildLocale} is not runtime-visible while its stage is ${managedLocaleDefinitions.get(managedBuildLocale)?.stage}`
            );
        }
        return resolveRequestedTag(canonicalBuildLocale, 'build', true);
    }

    const canonicalProfileLocale = canonicalizeLocaleTag(profileLanguageTag);
    if (canonicalProfileLocale && !isTestLocale(canonicalProfileLocale)) {
        return resolveRequestedTag(canonicalProfileLocale, 'profile', false);
    }

    for (const detectedLocaleTag of detectedLocaleTags) {
        const canonicalDetectedLocale = canonicalizeLocaleTag(detectedLocaleTag);
        if (canonicalDetectedLocale && !isTestLocale(canonicalDetectedLocale)) {
            return resolveRequestedTag(canonicalDetectedLocale, 'device', false);
        }
    }

    return {
        requestedTag: null,
        messageLocale: DEFAULT_LOCALE as MessageLocale,
        formatLocale: DEFAULT_FORMAT_LOCALE,
        direction: getLocaleDirection(DEFAULT_LOCALE as MessageLocale),
        source: 'default',
        fallbackUsed: false,
    };
}

function normalizedSnapshotPreference(value: string | null | undefined): string | null {
    const normalized = canonicalizeLocaleTag(value);
    return normalized && !isTestLocale(normalized) ? normalized : null;
}

function languageOf(localeTag: string): string {
    return new Intl.Locale(localeTag).language;
}

/** Resolve the immutable v1 payload shared with backend email delivery. */
export function resolveLocaleSnapshot({
    preferredLanguageTag,
    detectedPrimaryLocale,
}: ResolveLocaleSnapshotInput = {}): LocaleSnapshotV1 {
    const preferred = normalizedSnapshotPreference(preferredLanguageTag);
    const detected = normalizedSnapshotPreference(detectedPrimaryLocale);
    const requestedTag = preferred ?? detected;
    const formatTag = requestedTag ?? DEFAULT_FORMAT_LOCALE;
    const messageLocale = matchLiveMessageLocaleTag(formatTag) ?? DEFAULT_LOCALE;

    return {
        schema_version: LOCALE_SNAPSHOT_SCHEMA_VERSION,
        requested_tag: requestedTag,
        format_tag: formatTag,
        message_locale: messageLocale,
        source: preferred ? 'preferred' : detected ? 'detected' : 'default',
        message_fallback: languageOf(messageLocale) !== languageOf(formatTag),
    };
}

export function resolveAuthMessageLocale(
    input: ResolveAuthMessageLocaleInput = {},
    liveMessageLocales: readonly string[] = LIVE_MESSAGE_LOCALES
): string {
    const liveCandidates = new Map(
        liveMessageLocales.map((candidate) => [candidate, true])
    );
    const preferred = normalizedSnapshotPreference(input.preferredLanguageTag);
    const existing = matchSupportedLocaleTag(
        input.existingMessageLocale,
        liveCandidates
    );
    const detected = normalizedSnapshotPreference(input.detectedPrimaryLocale);
    const requested = preferred ?? existing ?? detected;
    return matchSupportedLocaleTag(requested, liveCandidates) ?? DEFAULT_LOCALE;
}

export function buildAuthMessageLocaleMetadata(
    input: ResolveAuthMessageLocaleInput = {},
    liveMessageLocales: readonly string[] = LIVE_MESSAGE_LOCALES
): Record<typeof AUTH_MESSAGE_LOCALE_METADATA_KEY, string> {
    return {
        [AUTH_MESSAGE_LOCALE_METADATA_KEY]: resolveAuthMessageLocale(
            input,
            liveMessageLocales
        ),
    };
}
